/**
 *
 * PROJECT eVessel
 * Developed by:  3WebBox LLC - 2023
 * 
 * Disclaimer: Please make sure to read related documentation before
 * making any changes to the code. Modify the code under your own
 * responsibility. for help please contact 3WebBox.
 * 
 * https://3webbox.com  : support@3webbox.com
 * 
 * 
 */

import { useState, useEffect, ReactElement } from 'react';

import { ApiFilled, CheckOutlined, CloseCircleFilled, DeleteFilled, DownloadOutlined, EditOutlined, ExportOutlined, FileAddFilled, InfoCircleOutlined, LeftCircleFilled, LoadingOutlined, MailFilled, MinusOutlined, PlusCircleFilled, PlusCircleOutlined, PlusOutlined, SearchOutlined, SendOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Checkbox, Col, DatePicker, Divider, Drawer, Empty, Form, Input, message, Modal, Popconfirm, Popover, Row, Segmented, Select, Space, Spin, Table, Tag, Typography } from "antd";

import EVAPI from '../../../lib/ev_lib/main';
import { config } from '../../../config';
import TextArea from 'antd/es/input/TextArea';
import { setClearFormFields } from '../../../util/clear_empty';
import { URLQuery } from '../../../util/url_queries';
import { userSettings } from '../../../lib/user_settings';

export interface CostTrackerRecordProp {
  uuid?:string|undefined,
  related_to?: string|undefined,
  related_to_uuid?: string|undefined,
  invoices_items_uuid?: string | undefined,
  item?: string|undefined,
  description?: string|undefined,
  price?: string | number,
  quantity?: string | number,
  billable?: 'y' | 'n',
  taxable?: 'y' | 'n',
  total?:number|string,
  clear?: string[] | undefined
}

export default function CostTrackerForm(props: any) {
  const formatter = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

  const [messageApi, contextHolder] = message.useMessage();

  const [form] = Form.useForm();

  const [notFound, setNotFound] = useState<boolean>(false);
  const [authorized, setAuthorized] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [openCostTrackerRecordForm, setOpenCostTrackerRecordForm] = useState<boolean>(false);

  const [errors, setErrors] = useState<any[] | null>(null);

  const [costTrackerTotalAmount, setCostTrackerTotalAmount] = useState<string|number>(0);
  const [costTrackerTotalBillableAmount, setCostTrackerTotalBillableAmount] = useState<string|number>(0);
  const [costTrackerTotalBilledAmount, setCostTrackerTotalBilledAmount] = useState<string|number>(0);
  const [costTrackerRecords, setCostTrackerRecords] = useState<CostTrackerRecordProp[]>([]);
  const [costTrackerRecord, setCostTrackerRecord] = useState<CostTrackerRecordProp | undefined>({
    related_to: props.relatedTo,
    related_to_uuid: props.relatedToUuid,
  });

  const [isLoadingAutoCompleteList, setIsLoadingAutoCompleteList] = useState<boolean>(false);
  const [autoCompleteList, setAutoCompleteList] = useState<any[]>([])
  const [autoCompleteSearchTerm, setAutoCompleteSearchTerm] = useState<string|null>(null);

  const [invoiceView, setInvoiceView] = useState<boolean>(false);

  let profile: any = localStorage.getItem('profile');
  let userPermissions:any = localStorage.getItem('user_permissions');
  let relatedTo: string | null = props?.relatedTo || null;
  let relatedToUuid: string | null = props?.relatedToUuid || null;
  
  useEffect(() => {
    try {
      if (typeof profile !== 'object') {
        profile = JSON.parse(profile);
      }

      if(typeof userPermissions !== 'object') {
        userPermissions = JSON.parse(userPermissions);
      }

      if (
        profile.type === 'management'
        && userPermissions.includes('manageCostTracker')
      ) {
        setAuthorized(true);
      }

      if (
        profile.type === 'management'
        && userPermissions.includes('manageInvoices')
        && props.invoiceView
      ) {
        setInvoiceView(true);
      }
    }
    catch (e) {
      console.warn(e);
    };

    if (relatedToUuid && relatedToUuid) getCostTrackerRecords();

    setTimeout(() => setIsReady(true), 1500);
  }, []);

  useEffect(() => {
    if(
      autoCompleteSearchTerm
      && autoCompleteSearchTerm?.length > 0
    ) {
      autoCompleteCostTrackerRecord();
    }
    else {
      setAutoCompleteList([]);
    }
  }, [autoCompleteSearchTerm]);

  useEffect(() => {
    getCostTrackerRecords();
  }, [props.reload])
  
  const autoCompleteCostTrackerRecord = async () => {
    if(!autoCompleteSearchTerm || isLoadingAutoCompleteList) return;
    setIsLoadingAutoCompleteList(true);

    var EV: any = new EVAPI;
    EV.debug = process.env.REACT_APP_MODE === "development" ? true : false;
    EV.baseAPI = URLQuery('targetDomain', window) || localStorage.getItem('target_domain');
    EV.authToken = localStorage.getItem('auth_token');
    EV.searchTerm = autoCompleteSearchTerm;
    EV.resPerPage = 3;
    EV.orderBy = 'cost_tracker.created_at';
    EV.orderDirection = 'DESC';

    var res: any = await EV.getCostTrackerRecords();

    if (res.data && res.data.records?.length > 0) {
      // Make sure ready is set here as well as the form will not be
      // rendered when the data is suppose to be set
      setAutoCompleteList(res.data.records);
    }

    else {
      setAutoCompleteList([]);
    }

    setIsLoadingAutoCompleteList(false);
    return;
  }

  const getCostTrackerRecords = async () => {
    setIsLoading(true);

    var EV: any = new EVAPI;
    EV.debug = process.env.REACT_APP_MODE === "development" ? true : false;
    EV.baseAPI = URLQuery('targetDomain', window) || localStorage.getItem('target_domain');
    EV.authToken = localStorage.getItem('auth_token');
    EV.relatedTo = props.relatedTo;
    EV.relatedToUuid = relatedToUuid;
    EV.showAll = 'y';
    
    var res: any = await EV.getCostTrackerRecords();
    
    setIsLoading(false);

    if (!res) {
      messageApi.open({
        type: 'error',
        content: 'Failed to pull invoice data from server',
      });
    }

    else if (res.code === 500) {
      messageApi.open({
        type: 'error',
        content: res?.errors[0]?.en
      });
    }

    else if (res.code === 403) {
      setAuthorized(false);
    }

    else if (res.data) {
      // Make sure ready is set here as well as the form will not be
      // rendered when the data is suppose to be set
      setIsReady(true);
      setCostTrackerRecords(res.data.records);
      setCostTrackerTotalAmount(res.data.total_amount);
      setCostTrackerTotalBillableAmount(res.data.total_billable_amount);
      setCostTrackerTotalBilledAmount(res.data.total_billed_amount);
    }

    return null;
  }

  const processCostTrackerRecord = async () => {
    if (isLoading) return null;
    setIsLoading(true);
    
    var EV: any = new EVAPI;
    EV.debug = process.env.REACT_APP_MODE === "development" ? true : false;
    EV.baseAPI = URLQuery('targetDomain', window) || localStorage.getItem('target_domain');
    EV.authToken = localStorage.getItem('auth_token');

    // handle cleared fields
    if (
      costTrackerRecord
      && costTrackerRecord.uuid
    ) {
      // check clearables
      costTrackerRecord.clear = setClearFormFields(costTrackerRecord) || undefined;
    }

    EV = Object.assign(EV, costTrackerRecord);

    // exempt the following fields in invoice view mode
    if(!invoiceView) {
      EV.related_to = props.relatedTo;
      EV.related_to_uuid = relatedToUuid;
    }

    var res: any = null;

    if (
      costTrackerRecord
      && costTrackerRecord.uuid
    ) res = await EV.updateCostTrackerRecord();
    else res = await EV.createCostTrackerRecord();

    setIsLoading(false);

    if (!res) {
      messageApi.open({
        type: 'error',
        content: 'Failed to complete the process',
      });
    }

    else if (res.status === 'fail') {
      setErrors(res.errors);
    }

    else if (res.code === 200) {
      messageApi.open({
        type: 'success',
        content: 'Process completed',
      });

      getCostTrackerRecords();
      setOpenCostTrackerRecordForm(false);
    }

    return null;
  }

  const deleteCostTrackerRecord = async (deleteUuid: string) => {
    if (isLoading) return null;
    setIsLoading(true);

    var EV: any = new EVAPI;
    EV.debug = process.env.REACT_APP_MODE === "development" ? true : false;
    EV.baseAPI = URLQuery('targetDomain', window) || localStorage.getItem('target_domain');
    EV.authToken = localStorage.getItem('auth_token');
    EV.uuid = deleteUuid;

    var res: any = res = await EV.deleteCostTrackerRecord();

    if (!res || res.status === 'fail') {
      messageApi.open({
        type: 'error',
        content: 'Failed to complete the process',
      });
    }

    else if (res.status === 'fail') {
      setErrors(res.errors);
    }

    else if (res.code === 200) {
      messageApi.open({
        type: 'success',
        content: 'Process completed',
      });

      getCostTrackerRecords();
    }

    setIsLoading(false);
    return null;
  }

  if(
    !authorized
    || !relatedTo
    || !relatedToUuid
  ) return null;

  return <>
    {contextHolder}

    {props.showTitle && <Typography.Title 
      level={props.titleLevel||1}
      children={'Cost Tracker'} 
    /> }

    {(errors && errors.length > 0) && <Alert
      type={'error'}
      message={'Errors submitting your data'}
      description={<ul>{errors.map((error, key) => {
        if(typeof error === 'object') {
          error = error.en
        }

        return <li>{error}</li>;
      })}</ul>}
      closable
      onClose={() => setErrors(null)}
      style={{ marginBottom: 20 }}
    />}

    <Modal
      open={openCostTrackerRecordForm}
      onCancel={() => setOpenCostTrackerRecordForm(false)}
      onOk={() => form.submit()}
      children={<>
        <Typography.Title
          level={4}
          children={"Cost Tracker Record"}
        />

        <Form
          form={form}
          layout="vertical"
          initialValues={costTrackerRecord}
          onFinish={processCostTrackerRecord}
        >
          <Form.Item
            label={'Item'}
            name={'item'}
            required
          >
            <Input
              placeholder={'Type here'}
              value={costTrackerRecord?.item}
              onChange={(e) => {
                setAutoCompleteSearchTerm(e.target.value);
                
                setCostTrackerRecord({
                  ...costTrackerRecord,
                  item: e.target.value
                })
              }}
            />
            {(autoCompleteList?.length > 0) && <>
              <Typography.Text strong type="secondary" className='small-font'>
                Found mathing items:
              </Typography.Text>

              {autoCompleteList.map((item, index) => {
                return <div 
                  className='item-search-selection'
                  onClick={() => {
                    setCostTrackerRecord({
                      ...costTrackerRecord,
                      item: item.item,
                      description: item.description,
                      quantity: item.quantity,
                      price: item.price,
                      taxable: item.taxable,
                      billable: item.billable
                    });

                    form.setFieldsValue({
                      ...costTrackerRecord,
                      item: item.item,
                      description: item.description,
                      quantity: item.quantity,
                      price: item.price,
                      taxable: item.taxable,
                      billable: item.billable
                    });

                    // clear the set at the end
                    setAutoCompleteList([]);
                  }}
                >
                  <Row>
                    <Col span={18}>{item.item}</Col>
                    <Col span={6} style={{textAlign: 'right'}}>
                        {formatter.format(item.price)}
                    </Col>
                    {item.description && <Col span={24}>
                      <Typography.Text italic className='small-font' type='secondary'>
                        {item.description}
                      </Typography.Text>
                    </Col>}
                  </Row>
                </div>
              })}
            </>}
          </Form.Item>
          <Form.Item
            label={'Description'}
            name={'description'}
          >
            <TextArea
              rows={2}
              placeholder={'Type here'}
              value={costTrackerRecord?.description}
              onChange={(e) => setCostTrackerRecord({
                ...costTrackerRecord,
                description: e.target.value
              })}
            />
          </Form.Item>
          <Form.Item
            style={{ marginBottom: 0 }}
            name={'taxable'}
          >
            <Checkbox
              checked={(costTrackerRecord?.taxable === 'y') ? true : false}
              onChange={(e) => {
                var value: 'y' | 'n' = 'n';
                var billable:'y'|'n' = costTrackerRecord?.billable||'n';

                if (e.target.checked) {
                  value = 'y';
                  billable = 'y';
                }

                setCostTrackerRecord({
                  ...costTrackerRecord,
                  taxable: value,
                  billable: billable
                });

                form.setFieldsValue({
                  ...costTrackerRecord,
                  taxable: value,
                  billable: billable
                });
              }}
              children={<Typography.Paragraph style={{ marginBottom: 0 }}>
                <Typography.Text strong>Taxable item </Typography.Text>
                <Typography.Text> - The item will be included in the invoice tax.</Typography.Text>
              </Typography.Paragraph>}
            />
          </Form.Item>
          <Form.Item
            name={'billable'}
          >
            <Checkbox
              checked={(costTrackerRecord?.billable === 'y') ? true : false}
              onChange={(e) => {
                var value: 'y' | 'n' = 'n';
                var taxable: 'y' | 'n' = costTrackerRecord?.taxable||'n';
                
                if (e.target.checked) {
                  value = 'y';
                }
                else {
                  taxable = 'n';
                }

                setCostTrackerRecord({
                  ...costTrackerRecord,
                  billable: value,
                  taxable: taxable
                });

                form.setFieldsValue({
                  ...costTrackerRecord,
                  billable: value,
                  taxable: taxable
                });
              }}
              children={<Typography.Paragraph style={{ marginBottom: 0 }}>
                <Typography.Text strong>Billable item </Typography.Text>
                <Typography.Text> - The item will be included in the invoice sub total.</Typography.Text>
              </Typography.Paragraph>}
            />
          </Form.Item>
          <Row gutter={[15, 15]}>
            <Col span={8}>
              <Form.Item
                label={'Price'}
                name={'price'}
                required
              >
                <Input
                  defaultValue={1}
                  placeholder={'Type here'}
                  value={costTrackerRecord?.price}
                  onChange={(e) => setCostTrackerRecord({
                    ...costTrackerRecord,
                    price: e.target.value
                  })}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={'Quantity'}
                name={'quantity'}
                required
              >
                <Input
                  defaultValue={1}
                  placeholder={'Type here'}
                  value={costTrackerRecord?.quantity}
                  onChange={(e) => setCostTrackerRecord({
                    ...costTrackerRecord,
                    quantity: e.target.value
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </>}
    />

    <Table
      size={userSettings('compact_view') ? 'small' : undefined}
      pagination={false}
      columns={[
        {
          key: 'uuid',
          dataIndex: 'uuid',
          title: "UUID",
          width: 170,
          fixed: true,
          render: (_:any, record:any) => {
            return record.uuid.substr(record.uuid.length - 10)
          }
        },
        (props.detailedListing) ? {
          key: 'related_to',
          dataIndex: 'related_ti',
          title: "Related To",
          width: 170,
          fixed: true,
          render: (_:any, record:any) => {
            return <div style={{textTransform: 'capitalize'}}>
              {record.related_to||'/** Error'}
            </div>
          }
        } : {},
        (props.detailedListing) ? {
          key: 'related_to_uuid',
          dataIndex: 'related_to_uuid',
          title: "Related UUID",
          width: 170,
          fixed: true,
          render: (_:any, record:any) => {
            return record.related_to_uuid?.substr(record.related_to_uuid.length - 10)||'/** Error'
          }
        } : {},
        {
          key: 'item_description',
          title: 'Item / Description',
          render: (_: any, record: any) => {
            return <div>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                <Typography.Text strong>{record.item}</Typography.Text>
              </Typography.Paragraph>
              {record.description && <Typography.Paragraph style={{ marginBottom: 0, marginTop: 5 }}>
                <Typography.Text>{record.description}</Typography.Text>
              </Typography.Paragraph>}
            </div>
          }
        },
        {
          key: 'invoices_uuid',
          title: 'Inv #',
          width: 100,
          render: (_: any, record: any) => {
            if(
              invoiceView
              && props.createInvoiceItem
            ) {

              if(record.invoices_items_uuid) return <Popconfirm
                title="Unlinking Cost Record"
                description="Are you sure you want to unlink the selected invoice item?"
                okText="Yes"
                cancelText="No"
                onConfirm={() => null}
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<MinusOutlined />}
                  children="Unlink"
                />
              </Popconfirm>
              
              else return <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                children="Invoice"
                onClick={() => {
                  props.createInvoiceItem(record);
                }}
              />
            }

            else if (record.invoices_uuid) return <Button
              type="link"
              size="small"
              icon={<ExportOutlined />}
              onClick={() => window.open(
                `/accounting/invoices/invoices/form/${record.invoices_uuid}`,
                '_blank'
              )}
            />
          }
        },
        {
          key: 'taxable',
          dataIndex: 'taxable',
          title: 'Taxable',
          width: 40,
          render: (_: any, record: any) => {
            if (record.taxable === 'y') return 'Yes'
            else if (record.taxable === 'n') return 'No'
            else return '-'
          }
        },
        {
          key: 'billable',
          dataIndex: 'billable',
          title: 'Billable',
          width: 40,
          render: (_: any, record: any) => {
            if (record.billable === 'y') return 'Yes'
            else if (record.billable === 'n') return 'No'
            else return '-'
          }
        },
        {
          key: 'price',
          dataIndex: 'price',
          title: 'Unit Price',
          align: 'right',
          width: 140,
          render: (_: any, record: any) => {
            return '$' + record.price?.toFixed(2);
          }
        },
        {
          key: 'quantity',
          dataIndex: 'quantity',
          title: 'Qty.',
          width: 80,
          align: 'right'
        },
        {
          key: 'total',
          dataIndex: 'total',
          title: 'Total (Tax excl.)',
          align: 'right',
          width: 180,
          render: (_: any, record: any) => {
            return '$' + record.total?.toFixed(2);
          }
        },
        {
          key: 'action',
          title: ' ',
          align: 'right',
          width: 100,
          render: (_: any, record: any) => {
            return <Space>
              <Button
                type='link'
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setCostTrackerRecord(record);
                  form.setFieldsValue(record);
                  setOpenCostTrackerRecordForm(true);
                }}
              />
              <Popconfirm
                title="Delete Cost Record"
                description="Are you sure to delete this cost?"
                okText="Yes"
                cancelText="No"
                onConfirm={() => {
                  deleteCostTrackerRecord(record.uuid)
                }}
              >
                <Button
                  type='link'
                  size="small"
                  icon={<CloseCircleFilled />}
                  danger
                />
              </Popconfirm>
            </Space>
          }
        }
      ]}
      dataSource={costTrackerRecords || []}
      summary={() => {
        if(invoiceView) return null;
        else return (
          <>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7} align='right' >
                <Typography.Text strong>Total Cost</Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Typography.Text strong>
                  ${Number(costTrackerTotalAmount).toFixed(2)}
                </Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2}></Table.Summary.Cell>
            </Table.Summary.Row>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7} align='right' >
                <Typography.Text>Total Billable Cost</Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Typography.Text>
                  ${Number(costTrackerTotalBillableAmount).toFixed(2)}
                </Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2}></Table.Summary.Cell>
            </Table.Summary.Row>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7} align='right' >
                <Typography.Text type="danger">Total Billed Cost</Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Typography.Text type="danger">
                  ${Number(costTrackerTotalBilledAmount).toFixed(2)}
                </Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2}></Table.Summary.Cell>
            </Table.Summary.Row>
          </>
        );
      }}
    />

    {props.showAddNew && <>
      <div style={{ marginBottom: 35 }} />

      <Space>
        <Button
          icon={<PlusCircleOutlined />}
          type={"primary"}
          onClick={() => {
            var defaultFormValues: CostTrackerRecordProp = {
              item: undefined,
              description: undefined,
              taxable: 'y',
              billable: 'y',
              price: 1,
              quantity: 1
            }

            setCostTrackerRecord(defaultFormValues);
            form.setFieldsValue(defaultFormValues);
            setOpenCostTrackerRecordForm(!openCostTrackerRecordForm);
          }}
        >
          Add Item
        </Button>
      </Space>
    </>}
  </>
}